// IR, as a league's rules have it (LINEUP.md "IR, and a roster that breaks its
// rules", D9): who may go there, who has to come off, what a roster over a
// limit is, and the sentences a lineup, a pickup and a waiver run say about
// them. ESPN, Yahoo and Sleeper agree on the rule: a player on IR counts
// toward no limit, and once he no longer qualifies the team adds nobody until
// he moves off — its lineup and its drops are never blocked.
//
// Pure and sport-free: the status words and the slot's name are handed in.

import type { RosterShape } from './legality';
import { rosterCapacity } from './legality';

/** A league's IR rule, as a host hands it in (`roster.reserve`, named). */
export interface ReserveRule {
  /** The reserve slot's id: `IR`. */
  slot: string;
  /** Its name as the sport says it: `IR`. */
  slotName: string;
  /** The injury statuses that may go there. */
  eligibleStatuses: readonly string[];
  /** Whether a player who no longer qualifies has to come off before the team adds anyone. */
  mustMoveWhenHealthy: boolean;
}

/** Whether a player with this injury status may go on (and stay on) the league's IR. */
export function qualifiesForReserve(
  rule: Pick<ReserveRule, 'eligibleStatuses'>,
  status: string | null,
): boolean {
  return status !== null && rule.eligibleStatuses.includes(status);
}

/**
 * The players on IR who no longer qualify and have to come off, in the order
 * given. Empty when the league lets them stay.
 */
export function mustLeaveReserve(
  rule: ReserveRule,
  held: Iterable<string>,
  statusOf: (playerId: string) => string | null,
): string[] {
  if (!rule.mustMoveWhenHealthy) return [];
  return [...held].filter((id) => !qualifiesForReserve(rule, statusOf(id)));
}

/** The roster less the players on IR: what every limit counts. */
export function activeRoster(roster: readonly string[], held: ReadonlySet<string>): string[] {
  return held.size === 0 ? [...roster] : roster.filter((id) => !held.has(id));
}

/** A limit the roster (less IR) is over: its size, or a position's maximum. */
export type OverLimit =
  | { kind: 'size'; count: number; cap: number }
  | { kind: 'position'; position: string; count: number; max: number };

/**
 * The first limit `active` is over, or null. Only what a drop fixes: a roster
 * short for its starting lineup is a slot that cannot score, not this.
 */
export function overLimit(
  active: readonly string[],
  shape: RosterShape,
  positionOf: (playerId: string) => string,
): OverLimit | null {
  const cap = rosterCapacity(shape);
  if (active.length > cap) return { kind: 'size', count: active.length, cap };
  const byPos = new Map<string, number>();
  for (const id of active) {
    const pos = positionOf(id);
    byPos.set(pos, (byPos.get(pos) ?? 0) + 1);
  }
  for (const [position, max] of Object.entries(shape.maxPerPosition)) {
    const count = byPos.get(position) ?? 0;
    if (count > max) return { kind: 'position', position, count, max };
  }
  return null;
}

/**
 * "The roster has 16 players and room for 15. This team can't add a player
 * until one is dropped." A dynasty's team after its rookie draft is commonly
 * several over (PLAN-2026-10-27-the-next-season §2.4 rule 5), and is told how
 * many.
 */
export function overLimitSentence(over: OverLimit): string {
  const fact =
    over.kind === 'size'
      ? `The roster has ${over.count} players and room for ${over.cap}.`
      : `The roster has ${over.count} ${over.position}, and the limit is ${over.max}.`;
  const past = over.kind === 'size' ? over.count - over.cap : over.count - over.max;
  return `${fact} This team can't add a player until ${past === 1 ? 'one is' : `${past} are`} dropped.`;
}

/**
 * Whom to let go, in `order`'s preference, to bring a roster within its size
 * and every position's limit (`overLimit`): one at a position over its limit
 * first, which brings the size down too, then the first of the rest. Fewer
 * than it needs when `order` runs out. A dynasty's team over both after its
 * rookie draft lets go no more than it has to (§2.4 rule 5).
 */
export function dropsToFitLimits(
  active: readonly string[],
  shape: RosterShape,
  positionOf: (playerId: string) => string,
  order: readonly string[],
): string[] {
  const out: string[] = [];
  let left = [...active];
  for (let n = 0; n < active.length; n += 1) {
    const byPos = new Map<string, number>();
    for (const id of left) byPos.set(positionOf(id), (byPos.get(positionOf(id)) ?? 0) + 1);
    const pastLimit = Object.entries(shape.maxPerPosition)
      .filter(([pos, max]) => (byPos.get(pos) ?? 0) > max)
      .map(([pos]) => pos);
    if (pastLimit.length === 0 && left.length <= rosterCapacity(shape)) break;
    const next = order.find(
      (id) =>
        left.includes(id) &&
        !out.includes(id) &&
        (pastLimit.length === 0 || pastLimit.includes(positionOf(id))),
    );
    if (next === undefined) break;
    out.push(next);
    left = left.filter((id) => id !== next);
  }
  return out;
}

/**
 * Why a team may add nobody: "Kelce is questionable now and can't stay on IR.
 * Until Kelce moves off it, this team can't add a player." The health bar, a
 * refused pickup, claim or trade, and a claim a run turns down all say it.
 *
 * NO INJURY LISTED, NOT "HEALTHY" (B-521): a player off the injury report may
 * still be his club's inactive for the week, and his row says *Out* beside a
 * sentence that called him healthy (the walk of 2026-10-26).
 */
export function reserveHoldSentence(
  rule: Pick<ReserveRule, 'slotName'>,
  name: string,
  status: string | null,
  nameOf: (status: string) => string,
): string {
  const now =
    status === null ? 'has no injury listed now' : `is ${nameOf(status).toLowerCase()} now`;
  return `${name} ${now} and can't stay on ${rule.slotName}. Until ${name} moves off it, this team can't add a player.`;
}

/**
 * Why a player cannot go on IR: "Only a player listed as out, injured reserve
 * or suspended can go on IR. Kelce is listed as questionable."
 */
export function reserveRefusal(
  rule: ReserveRule,
  name: string,
  status: string | null,
  nameOf: (status: string) => string,
): string {
  return `Only a player listed as ${reserveStatusList(rule, nameOf)} can go on ${rule.slotName}. ${name} ${
    status === null ? 'has no injury listed' : `is listed as ${nameOf(status).toLowerCase()}`
  }.`;
}

/** "out, injured reserve or suspended": the statuses that qualify, in the rule's order. */
export function reserveStatusList(
  rule: Pick<ReserveRule, 'eligibleStatuses'>,
  nameOf: (status: string) => string,
): string {
  const words = rule.eligibleStatuses.map((s) => nameOf(s).toLowerCase());
  if (words.length <= 1) return words[0] ?? 'nothing';
  return `${words.slice(0, -1).join(', ')} or ${words[words.length - 1]}`;
}

/**
 * A league's slots with its IR spots among them, and its taxi squad's. The
 * rules keep IR's count on `roster.reserve` and the taxi squad's on
 * `roster.taxi`, rather than in `roster.slots` (RULES_SCHEMA §6), so a
 * lineup, which lays out every slot, adds them here (D9;
 * PLAN-2026-10-27-the-next-season §2.7).
 */
export function slotsWithReserve(
  slots: readonly { slot: string; count: number }[],
  reserve: { slot: string; count: number } | null,
  taxi: { slot: string; count: number } | null = null,
): { slot: string; count: number }[] {
  const out = [...slots];
  for (const extra of [reserve, taxi])
    if (extra !== null && extra.count > 0 && !out.some((s) => s.slot === extra.slot))
      out.push({ slot: extra.slot, count: extra.count });
  return out;
}
